import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { processAssessment } from '../api.js';
import '../firebase-config';
import { getAttributionContext, getConversionTrackingData, getTrafficSource, trackMetaEvent } from '../utils/utm';
import {
  trackAssessmentCompleted,
  trackAssessmentStarted,
  trackGoogleAdsLeadFormConversion,
} from '../utils/marketingEvents';
import { runAssessmentSubmit } from './Assessment.submit.helpers';

// This is an opaque retry key, not an assessment, lead, or vendor identifier.
// It is kept with the recoverable form state so a response lost during a page
// reload cannot authorize a second Lead fan-out for the same submission.
const ASSESSMENT_REQUEST_ID_STORAGE_KEY = 'assessmentRequestId';
const ASSESSMENT_REQUEST_ID_RE = /^[A-Za-z0-9][A-Za-z0-9_-]{7,127}$/;

function readSavedAssessmentRequestId() {
  try {
    const value = window.localStorage.getItem(ASSESSMENT_REQUEST_ID_STORAGE_KEY) || '';
    return ASSESSMENT_REQUEST_ID_RE.test(value) ? value : '';
  } catch {
    return '';
  }
}

function clearSavedAssessmentRequestId() {
  try {
    window.localStorage.removeItem(ASSESSMENT_REQUEST_ID_STORAGE_KEY);
  } catch {
    // Private-storage failures leave assessment delivery available; the server
    // still treats the request identity as untrusted and validates App Check.
  }
}

function resolveInitialAssessmentForm(savedState, zipFromUrl) {
  const base = savedState ? JSON.parse(savedState) : {
    zipCode: '',
    propertyType: '',
    features: [],
    ownership: '',
    goals: [],
    email: ''
  };
  const validUrlZip = zipFromUrl && /^\d{5}$/.test(zipFromUrl);
  const urlZipOverridesSavedProgress = validUrlZip && base.zipCode !== zipFromUrl;

  return {
    formData: validUrlZip ? { ...base, zipCode: zipFromUrl } : base,
    urlZipOverridesSavedProgress,
  };
}

const Assessment = () => {
  // Load saved state from localStorage
  const savedState = localStorage.getItem('assessmentFormData');
  const savedStep = localStorage.getItem('assessmentCurrentStep');

  // Read ?zip= URL param — pre-fills Step 1 when coming from landing page
  const [searchParams] = useSearchParams();
  const zipFromUrl = searchParams.get('zip') || '';
  const initialForm = resolveInitialAssessmentForm(savedState, zipFromUrl);

  const [currentStep, setCurrentStep] = useState(savedStep ? parseInt(savedStep) : 1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [submitFailed, setSubmitFailed] = useState(false);
  const [showHelpTooltip, setShowHelpTooltip] = useState({});
  const assessmentRequestIdRef = useRef(
    initialForm.urlZipOverridesSavedProgress ? '' : readSavedAssessmentRequestId(),
  );
  const [formData, setFormData] = useState(initialForm.formData);

  const navigate = useNavigate();
  const totalSteps = 6;

  // Handle tooltip show/hide with auto-dismiss
  const toggleHelpTooltip = (step) => {
    setShowHelpTooltip(prev => ({
      ...prev,
      [step]: !prev[step]
    }));
    
    // Auto-dismiss after 6 seconds
    if (!showHelpTooltip[step]) {
      setTimeout(() => {
        setShowHelpTooltip(prev => ({
          ...prev,
          [step]: false
        }));
      }, 6000);
    }
  };

  // Save state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('assessmentFormData', JSON.stringify(formData));
  }, [formData]);

  useEffect(() => {
    localStorage.setItem('assessmentCurrentStep', currentStep.toString());
  }, [currentStep]);

  useEffect(() => {
    if (initialForm.urlZipOverridesSavedProgress) clearSavedAssessmentRequestId();
  }, [initialForm.urlZipOverridesSavedProgress]);

  useEffect(() => {
    const attributionContext = getAttributionContext();
    const startedStorageKey = `adu_assessment_started_${attributionContext?.attributionSessionId || 'direct'}`;

    try {
      if (sessionStorage.getItem(startedStorageKey)) return;
      sessionStorage.setItem(startedStorageKey, '1');
    } catch {
      // Tracking is best-effort and must never block assessment entry.
    }

    trackAssessmentStarted({
      ...attributionContext,
      surface: 'assessment_form',
    });
  }, []);

  // Handle browser back button
  useEffect(() => {
    const handlePopstate = (e) => {
      e.preventDefault();
      if (currentStep > 1) {
        setCurrentStep(prev => prev - 1);
      } else {
        if (window.confirm('Are you sure you want to leave? Your progress will be saved.')) {
          navigate('/');
        }
      }
    };

    window.addEventListener('popstate', handlePopstate);
    return () => window.removeEventListener('popstate', handlePopstate);
  }, [currentStep, navigate]);

  const [showClearProgress, setShowClearProgress] = useState(false);
  
  useEffect(() => {
    const hasProgress = Object.values(formData).some(value => 
      Array.isArray(value) ? value.length > 0 : value !== ''
    );
    setShowClearProgress(hasProgress);
  }, [formData]);

  const clearProgress = () => {
    if (window.confirm('Are you sure you want to start over? This will clear all your answers.')) {
      setFormData({
        zipCode: '',
        propertyType: '',
        features: [],
        ownership: '',
        goals: [],
        email: ''
      });
      setCurrentStep(1);
      localStorage.removeItem('assessmentFormData');
      localStorage.removeItem('assessmentCurrentStep');
      assessmentRequestIdRef.current = '';
      clearSavedAssessmentRequestId();
    }
  };

  // Handle Enter key to advance steps
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'Enter' && !isSubmitting) {
        e.preventDefault();
        if (validateCurrentStep()) {
          nextStep();
        }
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [currentStep, formData, isSubmitting]);

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;

    // A material edit after a failed submit starts a new submission. Keeping
    // the prior retry key would correctly fail closed at the server because
    // the request fingerprint no longer matches its completed/in-flight lease.
    assessmentRequestIdRef.current = '';
    clearSavedAssessmentRequestId();
    
    if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: prev[name].includes(value) 
          ? prev[name].filter(item => item !== value)
          : [...prev[name], value]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
      
      // Auto-advance removed for better user control
    }
  };

  const validateCurrentStep = () => {
    switch(currentStep) {
      case 1: return formData.zipCode.trim() !== '';
      case 2: return formData.propertyType !== '';
      case 3: return formData.features.length > 0;
      case 4: return formData.ownership !== '';
      case 5: return formData.goals.length > 0;
      case 6: return formData.email !== '';
      default: return false;
    }
  };

  const showValidationWarning = () => {
    const messages = {
      1: 'Please complete your ZIP code first.',
      2: 'Please select your property type first.',
      3: 'Please select at least one property feature first.',
      4: 'Please select ownership status first.',
      5: 'Please select at least one project goal first.'
    };
    alert(messages[currentStep] || 'Please complete all previous questions first.');
  };

  const nextStep = () => {
    if (currentStep < totalSteps) {
      if (validateCurrentStep()) {
        setCurrentStep(prev => prev + 1);
      } else {
        alert('Please answer this question before continuing.');
      }
    } else {
      handleSubmit();
    }
  };
  
  const validateAllSteps = () => {
    return formData.zipCode.trim() !== '' &&
           formData.propertyType !== '' &&
           formData.features.length > 0 &&
           formData.ownership !== '' &&
           formData.goals.length > 0;
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    if (!formData.email || !formData.email.includes('@')) {
      alert('Please enter a valid email address.');
      return;
    }

    // Guard against double-submit while a request is in flight.
    if (isSubmitting) {
      return;
    }

    setSubmitFailed(false); // Clear any prior failure before retrying
    setIsSubmitting(true);
    setShowLoader(true); // Show the loader overlay

    // Send data in the format the Cloud Function expects
    const trafficSource = getTrafficSource();
    const conversionTracking = getConversionTrackingData();
    const attributionContext = getAttributionContext();
    const assessmentData = {
      answers: {
        zip_code: formData.zipCode,
        property_type: formData.propertyType,
        features: formData.features,
        ownership: formData.ownership,
        goals: formData.goals,
        email: formData.email
      },
      timestamp: new Date().toISOString(),
      ...(trafficSource && { traffic_source: trafficSource }),
      ...(conversionTracking && { conversion_tracking: conversionTracking }),
      ...(attributionContext && { attribution_context: attributionContext }),
    };
    if (!assessmentRequestIdRef.current) {
      assessmentRequestIdRef.current = globalThis.crypto?.randomUUID
        ? globalThis.crypto.randomUUID()
        : `assessment-${Date.now()}-${Math.random().toString(16).slice(2)}`;
      try {
        localStorage.setItem(ASSESSMENT_REQUEST_ID_STORAGE_KEY, assessmentRequestIdRef.current);
      } catch {
        // Do not block the public assessment when browser storage is unavailable.
      }
    }

    // Normalizes thrown fetch errors, non-2xx responses, and a missing token
    // into a single { ok } outcome. Never throws.
    const outcome = await runAssessmentSubmit({
      processAssessmentFn: processAssessment,
      assessmentData,
      requestId: assessmentRequestIdRef.current,
    });

    if (outcome.ok) {
      const { publicToken } = outcome;
      const assessmentId = outcome.response?.assessmentId || outcome.response?.id || '';

      trackAssessmentCompleted({
        ...attributionContext,
        assessmentId,
        publicToken,
        surface: 'assessment_form',
      });

      // The server alone selects the vendor ID. This preserves CAPI/Pixel
      // dedupe after a lost response and retry, whose report assessment may
      // legitimately differ from the first assessment that fired CAPI.
      const leadEventId = typeof outcome.response?.conversionEventId === 'string'
        && /^assessment_[A-Za-z0-9_-]{1,256}$/.test(outcome.response.conversionEventId)
        ? outcome.response.conversionEventId
        : '';
      if (leadEventId) {
        trackMetaEvent(
          'Lead',
          {
            content_name: 'ADU Assessment',
            content_category: 'assessment',
            event_id: leadEventId,
          },
          { eventID: leadEventId }
        );
        trackGoogleAdsLeadFormConversion({ transactionId: leadEventId });
      }

      // Clear saved progress only after a confirmed successful submission.
      localStorage.removeItem('assessmentFormData');
      localStorage.removeItem('assessmentCurrentStep');
      assessmentRequestIdRef.current = '';
      clearSavedAssessmentRequestId();

      // Navigate directly to results page
      navigate(`/assessment/${publicToken}`);
      return;
    }

    // Failure: keep the lead's answers intact (localStorage untouched),
    // re-enable the submit control, and show the inline error + retry.
    console.error('Assessment submission did not complete:', outcome.error || 'no token returned');
    setIsSubmitting(false);
    setShowLoader(false);
    setSubmitFailed(true);
  };

  const progressPercent = ((currentStep - 1) / (totalSteps - 1)) * 100;

  const getStepTitle = () => {
    switch(currentStep) {
      case 1: return "What is your ZIP code?";
      case 2: return "Property Type";
      case 3: return "Property Features"; 
      case 4: return "Ownership Status";
      case 5: return "Project Goals";
      case 6: return "Get Your Results";
      default: return "ADU Feasibility Assessment";
    }
  };

  const getStepHelp = () => {
    switch(currentStep) {
      case 1: return "We use the ZIP Code to analyze local zoning laws and market conditions for your potential ADU project.";
      case 2: return "Allows us to run initial zoning and allowance ability without needing your actual project address.";
      case 3: return "Helps identify potential ADU conversion opportunities.";
      case 4: return "Checks for potential owner-occupancy requirements mandated by some localities without needing to know your specific property status.";
      case 5: return "The reason you may want or need an ADU lets us determine possible solutions; as an example, some localities allow ADUs by right if it is for a family member or caregiver.";
      case 6: return "We'll send your free analysis to your inbox, as well as display it on the next screen.";
      default: return "";
    }
  };

  return (
    <>
      <style>{`
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-in-out;
        }
        
        @keyframes fadeIn {
          from { 
            opacity: 0;
            transform: translateY(10px) scale(0.95);
          }
          to { 
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        
        @media (max-width: 640px) {
          .assessment-container {
            padding: 10px;
          }
        }
      `}</style>
      
    <div className="h-screen sm:min-h-screen flex items-center justify-center bg-[#f8f9fa] p-0 sm:p-6">
        <div className="bg-white rounded-none sm:rounded-[20px] shadow-lg border border-[#e0e0e0] w-full max-w-3xl h-full sm:h-auto sm:min-h-[600px] flex flex-col relative overflow-hidden">
          <div className="px-5 sm:px-10 pt-6 sm:pt-10 pb-4 sm:pb-8 border-b border-[#e0e0e0] bg-white flex-shrink-0">
            <div className="flex items-center gap-4">
              <div className="text-sm text-gray-600 font-medium" style={{ fontFamily: "'Roboto', sans-serif" }}>
                Step {currentStep} of {totalSteps}
              </div>
              <div className="flex-1">
                <div className="h-2 bg-[#f1f3f4] rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-[#1a73e8] transition-all duration-500 ease-out"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
              </div>
              {showClearProgress && currentStep > 1 && (
                <button 
                  onClick={clearProgress} 
                  className="text-sm text-[#1a73e8] hover:text-[#1557b0] transition-colors font-medium"
                  style={{ fontFamily: "'Roboto', sans-serif" }}
                >
                  Start Over
                </button>
              )}
            </div>
          </div>

          <div className="flex-1 px-5 sm:px-10 py-4 sm:py-6 flex flex-col overflow-y-auto">
            <div className="text-center mb-4">
              <h2 className="text-base font-medium text-gray-700" style={{ fontFamily: "'Google Sans', sans-serif", letterSpacing: '0.02em' }}>
                ADU Feasibility Assessment
              </h2>
            </div>
            
            {/* Help Modal */}
            {showHelpTooltip[currentStep] && (
              <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setShowHelpTooltip({})}>
                <div className="absolute inset-0 bg-black/30 backdrop-blur-sm"></div>
                <div className="relative bg-white rounded-2xl shadow-xl max-w-md w-full p-6 animate-fadeIn" onClick={(e) => e.stopPropagation()}>
                  <button
                    className="absolute top-4 right-4 w-8 h-8 rounded-full hover:bg-[#f1f3f4] transition-colors flex items-center justify-center"
                    onClick={() => setShowHelpTooltip({})}
                    aria-label="Close help"
                  >
                    <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#e8f0fe] flex items-center justify-center">
                      <svg className="w-5 h-5 text-[#1a73e8]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-medium text-gray-900 mb-2" style={{ fontFamily: "'Google Sans', sans-serif" }}>
                        Why we ask this
                      </h3>
                      <p className="text-sm text-gray-600 leading-relaxed" style={{ fontFamily: "'Roboto', sans-serif" }}>
                        {getStepHelp()}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <form onSubmit={(e) => e.preventDefault()} noValidate className="w-full max-w-xl mx-auto flex-1 flex flex-col">
              {/* Step 1: ZIP Code */}
              {currentStep === 1 && (
                <div className="animate-fadeIn flex-1 flex flex-col">
                  <div className="flex-1 flex flex-col justify-start sm:justify-center pt-8 sm:pt-0">
                    <h3 className="text-2xl sm:text-3xl font-medium text-gray-900 mb-6 sm:mb-10 text-center inline-flex items-center justify-center gap-2" style={{ fontFamily: "'Google Sans', sans-serif", letterSpacing: '0.02em' }}>
                      <span>What is your ZIP code?</span>
                      <button
                        className="inline-flex items-center justify-center w-8 h-8 rounded-full hover:bg-[#f1f3f4] transition-colors"
                        onClick={() => toggleHelpTooltip(1)}
                        aria-label="Get help"
                      >
                        <svg className="w-5 h-5 text-[#1a73e8]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </button>
                    </h3>
                    
                    <div className="w-full max-w-sm mx-auto">
                      <label htmlFor="zipCode" className="sr-only">ZIP code</label>
                      <input 
                        type="text" 
                        id="zipCode"
                        name="zipCode"
                        value={formData.zipCode}
                        onChange={handleInputChange}
                        className="w-full px-6 py-4 border-2 border-[#e0e0e0] bg-white text-gray-900 rounded-xl text-lg focus:outline-none focus:border-[#1a73e8] focus:ring-0 transition-all duration-200 text-center"
                        style={{ fontFamily: "'Roboto', sans-serif", fontSize: '18px' }}
                        placeholder="e.g., 90210"
                        aria-label="ZIP code"
                        aria-required="true"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength="5"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2: Property Type */}
              {currentStep === 2 && (
                <div className="animate-fadeIn flex-1 flex flex-col">
                  <div className="flex-1 flex flex-col justify-start sm:justify-center pt-8 sm:pt-0">
                    <h3 className="text-2xl sm:text-3xl font-medium text-gray-900 mb-6 sm:mb-10 text-center inline-flex items-center justify-center gap-2" style={{ fontFamily: "'Google Sans', sans-serif", letterSpacing: '0.02em' }}>
                      <span>Which of these best describes your property?</span>
                      <button
                        className="inline-flex items-center justify-center w-8 h-8 rounded-full hover:bg-[#f1f3f4] transition-colors"
                        onClick={() => toggleHelpTooltip(2)}
                        aria-label="Get help"
                      >
                        <svg className="w-5 h-5 text-[#1a73e8]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </button>
                    </h3>
                    
                    <div className="w-full max-w-lg mx-auto">
                      <div className="grid grid-cols-2 gap-3">
                        {['Single-Family', 'Two-Family', 'Three-Family', 'Multi-Family', 'Commercial', 'Other'].map((type) => {
                          const value = type.toLowerCase().replace('-', '-');
                          const isSelected = formData.propertyType === value;
                          return (
                            <label key={type} className="block w-full cursor-pointer" role="radio" aria-checked={isSelected}>
                              <input 
                                type="radio" 
                                name="propertyType" 
                                value={value}
                                onChange={handleInputChange}
                                className="hidden"
                                aria-label={`Property type: ${type}`}
                              />
                              <span className={`block w-full px-4 py-4 border-2 rounded-xl font-medium text-base transition-all duration-200 ${
                                isSelected 
                                  ? 'bg-[#e8f0fe] text-[#1a73e8] border-[#1a73e8]' 
                                  : 'bg-white text-gray-700 border-[#e0e0e0] hover:bg-[#f8f9fa] hover:border-gray-400'
                              }`} style={{ fontFamily: "'Roboto', sans-serif" }}>
                                {type}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3: Property Features */}
              {currentStep === 3 && (
                <div className="animate-fadeIn flex-1 flex flex-col">
                  <div className="flex-1 flex flex-col justify-start sm:justify-center pt-8 sm:pt-0">
                    <h3 className="text-2xl sm:text-3xl font-medium text-gray-900 mb-6 sm:mb-10 text-center inline-flex items-center justify-center gap-2" style={{ fontFamily: "'Google Sans', sans-serif", letterSpacing: '0.02em' }}>
                      <span>Select all property features that apply</span>
                      <button
                        className="inline-flex items-center justify-center w-8 h-8 rounded-full hover:bg-[#f1f3f4] transition-colors"
                        onClick={() => toggleHelpTooltip(3)}
                        aria-label="Get help"
                      >
                        <svg className="w-5 h-5 text-[#1a73e8]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </button>
                    </h3>
                    
                    <div className="w-full max-w-lg mx-auto">
                      <div className="grid grid-cols-2 gap-3">
                        {[
                          'Detached Garage',
                          'Basement Space',
                          'Side Yard or Backyard',
                          'Attached Garage',
                          'In-law Studio',
                          'Existing - Built'
                        ].map((feature) => {
                          const value = feature.toLowerCase().replace(/ /g, '-');
                          const isSelected = formData.features.includes(value);
                          return (
                            <label key={feature} className="block w-full cursor-pointer" role="checkbox" aria-checked={isSelected}>
                              <input 
                                type="checkbox" 
                                name="features" 
                                value={value}
                                onChange={handleInputChange}
                                className="hidden"
                                aria-label={`Property feature: ${feature}`}
                              />
                              <span className={`block w-full px-4 py-4 border-2 rounded-xl font-medium text-base transition-all duration-200 ${
                                isSelected 
                                  ? 'bg-[#e8f0fe] text-[#1a73e8] border-[#1a73e8]' 
                                  : 'bg-white text-gray-700 border-[#e0e0e0] hover:bg-[#f8f9fa] hover:border-gray-400'
                              }`} style={{ fontFamily: "'Roboto', sans-serif" }}>
                                {feature}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 4: Ownership Status */}
              {currentStep === 4 && (
                <div className="animate-fadeIn flex-1 flex flex-col">
                  <div className="flex-1 flex flex-col justify-start sm:justify-center pt-8 sm:pt-0">
                    <h3 className="text-2xl sm:text-3xl font-medium text-gray-900 mb-6 sm:mb-10 text-center inline-flex items-center justify-center gap-2" style={{ fontFamily: "'Google Sans', sans-serif", letterSpacing: '0.02em' }}>
                      <span>What is your ownership status?</span>
                      <button
                        className="inline-flex items-center justify-center w-8 h-8 rounded-full hover:bg-[#f1f3f4] transition-colors"
                        onClick={() => toggleHelpTooltip(4)}
                        aria-label="Get help"
                      >
                        <svg className="w-5 h-5 text-[#1a73e8]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </button>
                    </h3>
                    
                    <div className="w-full max-w-md mx-auto">
                      <div className="grid grid-cols-1 gap-3">
                        {[
                          'Own and Occupy',
                          'Own but Do Not Occupy',
                          'Considering Buying',
                          'Other'
                        ].map((status) => {
                          const value = status.toLowerCase().replace(/ /g, '-');
                          const isSelected = formData.ownership === value;
                          return (
                            <label key={status} className="block w-full cursor-pointer" role="radio" aria-checked={isSelected}>
                              <input 
                                type="radio" 
                                name="ownership" 
                                value={value}
                                onChange={handleInputChange}
                                className="hidden"
                                aria-label={`Ownership status: ${status}`}
                              />
                              <span className={`block w-full px-6 py-4 border-2 rounded-xl font-medium text-base transition-all duration-200 ${
                                isSelected 
                                  ? 'bg-[#e8f0fe] text-[#1a73e8] border-[#1a73e8]' 
                                  : 'bg-white text-gray-700 border-[#e0e0e0] hover:bg-[#f8f9fa] hover:border-gray-400'
                              }`} style={{ fontFamily: "'Roboto', sans-serif" }}>
                                {status}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 5: Project Goals */}
              {currentStep === 5 && (
                <div className="animate-fadeIn flex-1 flex flex-col">
                  <div className="flex-1 flex flex-col justify-start sm:justify-center pt-8 sm:pt-0">
                    <h3 className="text-2xl sm:text-3xl font-medium text-gray-900 mb-2 text-center inline-flex items-center justify-center gap-2" style={{ fontFamily: "'Google Sans', sans-serif", letterSpacing: '0.02em' }}>
                      <span>What are your main goals for building an ADU?</span>
                      <button
                        className="inline-flex items-center justify-center w-8 h-8 rounded-full hover:bg-[#f1f3f4] transition-colors"
                        onClick={() => toggleHelpTooltip(5)}
                        aria-label="Get help"
                      >
                        <svg className="w-5 h-5 text-[#1a73e8]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </button>
                    </h3>
                    <p className="text-sm text-gray-600 text-center mb-6 sm:mb-8" style={{ fontFamily: "'Roboto', sans-serif" }}>(Select all that apply)</p>
                    
                    <div className="w-full max-w-lg mx-auto">
                      <div className="grid grid-cols-2 gap-3">
                        {[
                          'Rental Income',
                          'Family/Caregiver',
                          'Aging in Place',
                          'Property Value',
                          'Home Office',
                          'Other'
                        ].map((goal) => {
                          const value = goal.toLowerCase().replace(/ /g, '-');
                          const isSelected = formData.goals.includes(value);
                          return (
                            <label key={goal} className="block w-full cursor-pointer" role="checkbox" aria-checked={isSelected}>
                              <input 
                                type="checkbox" 
                                name="goals" 
                                value={value}
                                onChange={handleInputChange}
                                className="hidden"
                                aria-label={`Goal: ${goal}`}
                              />
                              <span className={`block w-full px-4 py-4 border-2 rounded-xl font-medium text-base transition-all duration-200 ${
                                isSelected 
                                  ? 'bg-[#e8f0fe] text-[#1a73e8] border-[#1a73e8]' 
                                  : 'bg-white text-gray-700 border-[#e0e0e0] hover:bg-[#f8f9fa] hover:border-gray-400'
                              }`} style={{ fontFamily: "'Roboto', sans-serif" }}>
                                {goal}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 6: Email */}
              {currentStep === 6 && (
                <div className="animate-fadeIn flex-1 flex flex-col">
                  <div className="flex-1 flex flex-col justify-start sm:justify-center pt-8 sm:pt-0">
                    <h3 className="text-2xl sm:text-3xl font-medium text-gray-900 mb-6 sm:mb-10 text-center inline-flex items-center justify-center gap-2" style={{ fontFamily: "'Google Sans', sans-serif", letterSpacing: '0.02em' }}>
                      <span>Where should we send your ADU feasibility assessment?</span>
                      <button
                        className="inline-flex items-center justify-center w-8 h-8 rounded-full hover:bg-[#f1f3f4] transition-colors"
                        onClick={() => toggleHelpTooltip(6)}
                        aria-label="Get help"
                      >
                        <svg className="w-5 h-5 text-[#1a73e8]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </button>
                    </h3>
                    
                    <div className="w-full max-w-sm mx-auto">
                      <label htmlFor="email" className="sr-only">Email address</label>
                      <input 
                        type="email" 
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full px-6 py-4 border-2 border-[#e0e0e0] bg-white text-gray-900 rounded-xl text-lg focus:outline-none focus:border-[#1a73e8] focus:ring-0 transition-all duration-200"
                        style={{ fontFamily: "'Roboto', sans-serif", fontSize: '18px' }}
                        placeholder="your@email.com"
                        aria-label="Email address"
                        aria-required="true"
                        aria-describedby="email-description"
                        autoComplete="email"
                      />
                      <p id="email-description" className="text-sm mt-4 text-center" style={{ fontFamily: "'Roboto', sans-serif", color: '#5f6368' }}>
                        We'll send your free analysis to your inbox and display it on the next screen. By clicking Submit, you agree to receive this email and acknowledge our <a href="/legal/assessment-disclaimer" target="_blank" rel="noopener noreferrer" className="hover:underline" style={{ color: '#5f6368', fontWeight: '500' }}>Free Assessment Disclaimer</a> and <a href="/legal" target="_blank" rel="noopener noreferrer" className="hover:underline" style={{ color: '#5f6368', fontWeight: '500' }}>Terms &amp; Conditions</a>.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </form>
          </div>
          
          <div className="px-5 sm:px-10 py-5 sm:py-8 border-t border-[#e0e0e0] bg-white flex-shrink-0">
            {submitFailed && (
              <div
                role="alert"
                aria-live="assertive"
                className="max-w-xl mx-auto w-full mb-5 rounded-xl border border-[#f3c0bb] bg-[#fce8e6] px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-4"
              >
                <div className="flex items-start gap-3 flex-1">
                  <svg className="w-5 h-5 mt-0.5 flex-shrink-0 text-[#c5221f]" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                  </svg>
                  <div>
                    <p className="text-sm font-medium text-[#c5221f]" style={{ fontFamily: "'Google Sans', sans-serif" }}>
                      We couldn't process your assessment.
                    </p>
                    <p className="text-sm mt-1 text-gray-700" style={{ fontFamily: "'Roboto', sans-serif" }}>
                      Your answers are still here — nothing was lost.
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="self-start sm:self-auto flex-shrink-0 px-6 py-2.5 bg-[#1a73e8] hover:bg-[#1557b0] text-white rounded-xl font-medium text-sm transition-all hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ fontFamily: "'Google Sans', sans-serif", letterSpacing: '0.02em' }}
                >
                  {isSubmitting ? 'Processing...' : 'Try again'}
                </button>
              </div>
            )}
            <div className="flex justify-between items-center max-w-xl mx-auto w-full">
              <button
                type="button"
                onClick={prevStep}
                className={`px-6 py-3 rounded-xl font-medium text-gray-600 transition-all hover:bg-[#f1f3f4] ${currentStep === 1 ? 'invisible' : ''}`}
                style={{ fontFamily: "'Google Sans', sans-serif", letterSpacing: '0.02em' }}
              >
                ← Back
              </button>
              <button
                type="button"
                onClick={nextStep}
                disabled={isSubmitting}
                className="px-8 py-3 bg-[#1a73e8] hover:bg-[#1557b0] text-white rounded-xl font-medium transition-all hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed min-w-[120px]"
                style={{ fontFamily: "'Google Sans', sans-serif", letterSpacing: '0.02em' }}
              >
                {isSubmitting ? 'Processing...' : (currentStep === totalSteps ? 'Submit' : 'Next →')}
              </button>
            </div>
          </div>
        </div>
    </div>
    
    {/* Loading Overlay */}
    {showLoader && (
      <div className="fixed inset-0 z-50 bg-white flex items-center justify-center">
        <style>{`
          .spinner {
            width: 56px;
            height: 56px;
            display: grid;
            border: 4px solid #0000;
            border-radius: 50%;
            border-right-color: #1a73e8;
            animation: tri-spinner 1s infinite linear;
          }

          .spinner::before,
          .spinner::after {
            content: "";
            grid-area: 1/1;
            margin: 2px;
            border: inherit;
            border-radius: 50%;
            animation: tri-spinner 2s infinite;
          }

          .spinner::after {
            margin: 8px;
            animation-duration: 3s;
          }

          @keyframes tri-spinner {
            100% {
              transform: rotate(1turn);
            }
          }

          .loader-text {
            color: #4a4a4a;
            font-family: 'Google Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            font-weight: 500;
            font-size: 25px;
            height: 40px;
            padding: 10px 10px;
            display: flex;
            border-radius: 8px;
            margin-top: 20px;
            justify-content: center;
          }

          .words {
            overflow: hidden;
            height: 40px;
            padding-left: 8px;
          }

          .word {
            display: block;
            height: 100%;
            padding-left: 6px;
            color: #1a73e8;
            animation: cycle-words 9s infinite;
            line-height: 40px;
          }

          @keyframes cycle-words {
            0% { transform: translateY(0%); }
            11.11% { transform: translateY(-100%); }
            22.22% { transform: translateY(-200%); }
            33.33% { transform: translateY(-300%); }
            44.44% { transform: translateY(-400%); }
            55.55% { transform: translateY(-500%); }
            66.66% { transform: translateY(-600%); }
            77.77% { transform: translateY(-700%); }
            88.88% { transform: translateY(-800%); }
            100% { transform: translateY(-900%); }
          }
        `}</style>

        <div className="text-center max-w-xl px-6">
          <div className="flex flex-col items-center mb-8">
            <div className="spinner" />
            <div className="loader-text">
              <span>Generating Assessment</span>
              <div className="words">
                <span className="word">Summary</span>
                <span className="word">Goals</span>
                <span className="word">Zoning</span>
                <span className="word">Site</span>
                <span className="word">Design</span>
                <span className="word">Economics</span>
                <span className="word">Timeline</span>
                <span className="word">Impact</span>
                <span className="word">Actions</span>
                <span className="word">Summary</span>
              </div>
            </div>
          </div>
          
        </div>
      </div>
    )}
    
    </>
  );
};

export default Assessment;
